import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import type {
  CredentialDetail,
  CredentialSummary,
  SyncReport,
  VaultStatus,
} from '@internal/plugin-credentials-common';

export interface CredentialsApi {
  getStatus(): Promise<VaultStatus>;
  list(): Promise<CredentialSummary[]>;
  read(path: string): Promise<CredentialDetail>;
  download(path: string): Promise<{ filename: string; content: string }>;
  sync(): Promise<SyncReport>;
  listMounts(): Promise<{ mounts: string[]; default: string }>;
  create(
    path: string,
    data: Record<string, string>,
    opts?: { mount?: string; overwrite?: boolean },
  ): Promise<void>;
  remove(path: string, mount?: string): Promise<void>;
}

export const credentialsApiRef = createApiRef<CredentialsApi>({
  id: 'plugin.credentials.service',
});

export class CredentialsClient implements CredentialsApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async base(): Promise<string> {
    return await this.discoveryApi.getBaseUrl('credentials');
  }

  /** Unwraps Backstage's error envelope so the real reason reaches the UI. */
  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const res = await this.fetchApi.fetch(`${await this.base()}${path}`, init);
    if (!res.ok) {
      let detail = `${res.status} ${res.statusText}`;
      try {
        const body = await res.json();
        if (body?.error?.message) detail = body.error.message;
      } catch {
        // Non-JSON body; the status line is all we have.
      }
      throw new Error(detail);
    }
    return (await res.json()) as T;
  }

  async getStatus() {
    return this.request<VaultStatus>('/status');
  }

  async list() {
    const r = await this.request<{ items: CredentialSummary[] }>('/credentials');
    return r.items;
  }

  async read(path: string) {
    return this.request<CredentialDetail>(
      `/credentials/${path.split('/').map(encodeURIComponent).join('/')}`,
    );
  }

  async download(path: string) {
    const encoded = path.split('/').map(encodeURIComponent).join('/');
    const res = await this.fetchApi.fetch(
      `${await this.base()}/credentials/${encoded}?download=true`,
    );
    if (!res.ok) {
      throw new Error(`${res.status} ${res.statusText}`);
    }

    const disposition = res.headers.get('Content-Disposition') ?? '';
    const match = /filename="([^"]+)"/.exec(disposition);
    return {
      filename: match?.[1] ?? path.split('/').pop()!,
      content: await res.text(),
    };
  }

  async sync() {
    return this.request<SyncReport>('/sync', { method: 'POST' });
  }

  async listMounts() {
    return this.request<{ mounts: string[]; default: string }>('/mounts');
  }

  async create(
    path: string,
    data: Record<string, string>,
    opts: { mount?: string; overwrite?: boolean } = {},
  ) {
    await this.request<void>('/credentials', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ path, data, ...opts }),
    });
  }

  async remove(path: string, mount?: string) {
    const encoded = path.split('/').map(encodeURIComponent).join('/');
    const q = mount ? `?mount=${encodeURIComponent(mount)}` : '';
    const res = await this.fetchApi.fetch(
      `${await this.base()}/credentials/${encoded}${q}`,
      { method: 'DELETE' },
    );
    if (!res.ok && res.status !== 204) {
      let detail = `${res.status} ${res.statusText}`;
      try {
        const body = await res.json();
        if (body?.error?.message) detail = body.error.message;
      } catch {
        // Non-JSON body; the status line is all we have.
      }
      throw new Error(detail);
    }
  }
}
